import {
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import {
  SmartAutoComplete,
  SmartCheckbox,
  SmartDateTimePicker,
  SmartForm,
  useSmartForm,
} from "@nodeon/react-components";
import { roundToNearestMinutes } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BerthOrderDetailsDto,
  BerthOrderListDto,
  useBerthApi,
  useThirdPartyApi,
} from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";

const dataToForm = (
  x: Partial<BerthOrderDetailsDto> | undefined,
): BerthOrderFormInputs => {
  return {
    ...x,
    berthId: x?.berthId ?? null,
    startTimestamp:
      x?.startTimestamp ?? roundToNearestMinutes(new Date(), { nearestTo: 30 }),
    endTimestamp:
      x?.endTimestamp ?? roundToNearestMinutes(new Date(), { nearestTo: 30 }),
    cordMen: x?.cordMen ?? false,
    water: x?.water ?? false,
    power: x?.power ?? false,
    garbage: x?.garbage ?? false,
    vesselId: x?.vesselId ?? null,
  };
};

export interface BerthOrderFormInputs {
  startTimestamp: Date;
  endTimestamp: Date;
  cordMen: boolean;
  water: boolean;
  power: boolean;
  garbage: boolean;
  vesselId: number | null;
  berthId: number | null;
}

interface Props {
  onSubmit: (data: BerthOrderFormInputs) => void;
  readOnly?: boolean;
  defaultValues?: Partial<BerthOrderDetailsDto>;
  formId: string;
  initialBerthId: number | null;
  portId?: number;
  berthName?: string;
}

export const BerthOrderForm = ({
  onSubmit,
  readOnly,
  formId,
  defaultValues,
  initialBerthId,
  portId,
  berthName,
}: Props): JSX.Element => {
  const { t } = useTranslation("berths");
  const dateFormatter = useDateFormatter();

  const formDefaultValues = useMemo(
    () => dataToForm(defaultValues),
    [defaultValues],
  );

  const formMethods = useSmartForm({
    defaultValues: formDefaultValues,
    mode: "onSubmit",
  });
  const { setValue } = formMethods;
  const { getAISItems } = useThirdPartyApi();
  const { getBerths, getBerthOrders } = useBerthApi();

  const { data: vessels } = useCustomQuery("vessels", () => getAISItems());

  const { watch } = formMethods;
  const formBerthId = watch("berthId");
  const startTimestamp = watch("startTimestamp");
  const endTimestamp = watch("endTimestamp");

  const { data: berths } = useCustomQuery(
    { key: "berths-for-port", id: portId },
    () => getBerths({ id: portId }),
    {
      select: (d) => d.results,
      enabled: !!portId && !initialBerthId,
    },
  );

  const { data: berthOrders } = useCustomQuery(
    { key: "berth-orders", id: formBerthId },
    () => getBerthOrders(formBerthId!),
    {
      enabled: !!formBerthId,
    },
  );

  // Try finding a matching berth name, if a name string was given
  React.useEffect(() => {
    if (initialBerthId) setValue("berthId", initialBerthId);
    if (!berthName || !berths) return;
    const berth = berths.find(
      (b) => b.name?.toLowerCase()?.trim() === berthName.toLowerCase().trim(),
    );
    if (!berth) return;
    setValue("berthId", berth.id);
  }, [berthName, berths, initialBerthId, portId, setValue]);

  // Check if there are overlapping orders
  const overlappingOrders: BerthOrderListDto[] = React.useMemo(() => {
    if (startTimestamp?.valueOf() > endTimestamp?.valueOf()) return [];
    return (
      berthOrders?.results.filter(
        (o) =>
          (o.startTimestamp >= startTimestamp &&
            o.startTimestamp < endTimestamp) ||
          (o.endTimestamp > startTimestamp && o.endTimestamp <= endTimestamp) ||
          (o.startTimestamp <= startTimestamp &&
            o.endTimestamp >= endTimestamp) ||
          (o.startTimestamp === startTimestamp &&
            o.endTimestamp === endTimestamp),
      ) ?? []
    );
  }, [startTimestamp, endTimestamp, berthOrders?.results]);

  return (
    <Container maxWidth={false}>
      <SmartForm<BerthOrderFormInputs>
        onSubmit={onSubmit}
        noGrid
        methods={formMethods}
        formId={formId}
      >
        <Grid container spacing={1}>
          <Grid container flexDirection="row" spacing={1} item xs={12}>
            <Grid item xs={8}>
              <SmartAutoComplete<
                BerthOrderFormInputs,
                number,
                false,
                true,
                false
              >
                name="vesselId"
                options={vessels?.map((x) => x.mmsi) ?? []}
                controllerProps={{
                  rules: {
                    required: { value: true, message: t("form:required") },
                  },
                }}
                label={t("vessels:vessel")}
                fullWidth
                getOptionLabel={(opt) => {
                  const vessel = vessels?.find((x) => x.mmsi === opt);
                  if (!vessel) return t("general:unknown");
                  return vessel.name
                    ? `${vessel.name} - ${vessel.callSign}`
                    : t("general:unknown");
                }}
                disableClearable
              />
            </Grid>
            <Grid item xs={8}>
              {!initialBerthId ? (
                <SmartAutoComplete<
                  BerthOrderFormInputs,
                  number,
                  false,
                  true,
                  false
                >
                  name="berthId"
                  options={berths?.map((x) => x.id) ?? []}
                  controllerProps={{
                    rules: {
                      required: { value: true, message: t("form:required") },
                    },
                  }}
                  label={t("berth")}
                  fullWidth
                  gridProps={{ xs: 8, lg: 6 }}
                  getOptionLabel={(opt) => {
                    const berth = berths?.find((x) => x.id === opt);
                    if (!berth) return t("general:unknown");
                    return berth.name ?? t("general:unknown");
                  }}
                  disableClearable
                />
              ) : null}
            </Grid>
          </Grid>
          <Grid container spacing={1} item xs={7}>
            <Grid item xs={8}>
              <SmartDateTimePicker<BerthOrderFormInputs>
                label={t("startDate")}
                name="startTimestamp"
                minutesStep={30}
              />
            </Grid>
            <Grid item xs={8}>
              <SmartDateTimePicker<BerthOrderFormInputs>
                label={t("endDate")}
                name="endTimestamp"
                minutesStep={30}
              />
            </Grid>
            <Grid container spacing={1} item flexDirection="column">
              <Grid item>
                <SmartCheckbox
                  name="cordMen"
                  label={t("cordMen")}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item>
                <SmartCheckbox
                  name="power"
                  label={t("power")}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item>
                <SmartCheckbox
                  name="garbage"
                  label={t("garbage")}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item>
                <SmartCheckbox
                  name="water"
                  label={t("water")}
                  disabled={readOnly}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container item spacing={1} xs={5}>
            {overlappingOrders?.length ? (
              <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                <Typography variant="h6">{t("overlapping-orders")}</Typography>
                <List>
                  {overlappingOrders?.map((o) => (
                    <ListItem key={o.id}>
                      <ListItemText
                        primary={`${o.vessel?.name} - ${o.vessel?.callSign}`}
                        secondary={`${dateFormatter({
                          date: o.startTimestamp,
                        })} - ${dateFormatter({
                          date: o.endTimestamp,
                        })}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </SmartForm>
    </Container>
  );
};
